import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import { Ellipsis } from '@openx/components-core/lib/Ellipsis/Ellipsis';
import { Loader } from '@openx/components-core/lib/Loader/Loader';
import { StatusCell } from '@openx/components-deal/lib/DealsList/parts/StatusCell';
import type { Deal } from '@openx/types/deal';
import groupBy from 'lodash/groupBy';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { GetAudiencesListQuery } from '../../../../graphql/query/audiences/GetAudiencesList.generated';
import { useDealsUsedByAudiences } from '../../hooks/useDealsUsedByAudiences';

type ArchiveAudienceDialogContentProps = {
  setConfirmationBtnDisabled: (state: boolean) => void;
  audiences: Array<Pick<GetAudiencesListQuery['audience'][number], 'id' | 'name'>>;
};

export const ArchiveAudienceDialogContent = ({
  audiences,
  setConfirmationBtnDisabled,
}: ArchiveAudienceDialogContentProps) => {
  const { t } = useTranslation();

  const audienceIds = audiences.map(({ id }) => id);
  const { error: dealsError, loading: dealsLoading, deals } = useDealsUsedByAudiences(audienceIds);

  useEffect(() => {
    if (!dealsLoading) {
      setConfirmationBtnDisabled(false);
    }
  }, [dealsLoading, setConfirmationBtnDisabled]);

  const audienceIdToDeals = useMemo(() => groupBy(deals, deal => deal.audience_id), [deals]);

  const audiencesWithDeals = useMemo(
    () =>
      audiences
        .filter(audience => Object.keys(audienceIdToDeals).includes(audience.id))
        .map(({ id, name }) => ({ name, deals: audienceIdToDeals[id].map(deal => deal.obj), id })),
    [audiences, audienceIdToDeals]
  );

  const audiencesWithoutDeals = useMemo(
    () =>
      audiences
        .filter(audience => !Object.keys(audienceIdToDeals).includes(audience.id))
        .map(({ id, name }) => ({ id, name })),
    [audiences, audienceIdToDeals]
  );

  if (dealsLoading) {
    return <Loader />;
  }

  if (dealsError) {
    // biome-ignore lint/suspicious/noConsole: logging error in console
    console.error('Error fetching deals data');
  }

  return (
    <Box sx={{ borderTop: theme => `1px solid ${theme.palette.divider}`, py: 4 }}>
      <Typography color="textPrimary" sx={{ px: 2 }}>
        {t("You're about to archive {quantifier} Audience{plural}", {
          quantifier: audiences.length > 1 ? 'several ' : '',
          plural: audiences.length > 1 ? 's' : '',
        })}
      </Typography>

      {audiencesWithDeals.length > 0 && (
        <Box
          sx={{
            backgroundColor: 'background.boxLight',
            borderTop: theme => `1px solid ${theme.palette.divider}`,
            borderBottom: theme => `1px solid ${theme.palette.divider}`,
            mt: 2,
            pb: 2,
          }}
        >
          <Alert
            severity="warning"
            sx={{
              '& .MuiAlert-message': { color: 'warning.dark' },
            }}
          >
            {t('The following deal IDs will be affected by the change.')}
          </Alert>

          {audiencesWithDeals.map(audience => (
            <Box
              key={audience.id}
              sx={{
                px: 2,
              }}
            >
              <Typography
                noWrap
                gutterBottom
                color="textPrimary"
                sx={{
                  fontWeight: 700,
                }}
              >
                {audience.name}
              </Typography>
              <DealList deals={audience.deals} />
            </Box>
          ))}
        </Box>
      )}

      {audiencesWithoutDeals.length > 0 && (
        <Box
          sx={{
            ...(audiencesWithDeals.length > 0 && {
              paddingTop: theme => theme.spacing(4),
            }),
            width: '100%',
            '&:not(:last-child)': {
              paddingBottom: 2,
            },
            px: 2,
          }}
        >
          {audiencesWithoutDeals.map(({ id, name }) => (
            <Typography
              color="textPrimary"
              noWrap
              key={id}
              sx={{
                fontWeight: 700,
                mt: 0.6,
              }}
            >
              {name}
            </Typography>
          ))}
        </Box>
      )}
    </Box>
  );
};

type DealListProps = {
  deals: Deal[];
};

const DealList = ({ deals }: DealListProps) => {
  if (!deals.length) {
    return null;
  }

  return (
    <Grid container sx={{ flexDirection: 'column' }}>
      {deals.map(({ id, status, name, deal_id }) => {
        return (
          <Grid container key={id}>
            <Grid size={5}>
              <Typography color="textPrimary" data-test="deal-name">
                <Ellipsis width="90%" tooltip>
                  {name}
                </Ellipsis>
              </Typography>
            </Grid>
            <Grid size={4}>
              <Typography color="textPrimary" data-test="exchange-deal-id">
                {deal_id}
              </Typography>
            </Grid>
            <Grid size={3}>
              <StatusCell status={status ?? ''} />
            </Grid>
          </Grid>
        );
      })}
    </Grid>
  );
};
